/**
 * Fire-and-forget instrumentation — a failed report must NEVER block or error the
 * applicant. Never send image bytes.
 *
 * This is the ONE network surface for customer-attempt reporting: every trigger
 * point in the flow funnels through {@link reportAttempt} so the endpoint stays
 * swappable in a single place. It POSTs a failed capture (a self-retake or a
 * server-rejected upload) to `/api/v1/collection-flow/attempts`, matching the
 * URL + bearer conventions in ./client (`API_BASE` + `authorization: Bearer`).
 *
 * Transport: `fetch(..., { keepalive: true })` — NOT `navigator.sendBeacon`.
 * The endpoint authenticates the case-bound applicant token from the
 * `authorization: Bearer` header (see the server's `applicant_auth`), and
 * `sendBeacon` cannot set request headers, so a beacon would arrive
 * unauthenticated (401) and never record. `keepalive: true` gives the same
 * unload-safe delivery while still carrying the bearer header. The whole call is
 * wrapped in try/catch and swallowed: a throw or rejected promise must never
 * propagate to the applicant path.
 */
import type {
  AttemptStepKey,
  DocumentReason,
  GeolocationReason,
  LivenessReason,
} from './attemptReason';

const API_BASE = import.meta.env.FRONTEND_API_BASE?.replace(/\/$/, '') ?? '';
const ATTEMPTS_URL = `${API_BASE}/api/v1/collection-flow/attempts`;

/**
 * Idempotency guard across the process lifetime: a blocked-submit followed by a
 * retake on the SAME logical attempt shares one `attemptKey`, so it is counted
 * once. (The server ALSO dedups on UNIQUE(case, attempt_key); this is the
 * client-side belt to avoid an unnecessary second request.)
 */
const sent = new Set<string>();

export interface AttemptReport {
  stepKey: AttemptStepKey;
  /**
   * `geolocation` is not a capture — nothing is photographed and nothing is
   * uploaded. It rides this channel because the shape is identical and the need
   * is the same: without it, a browser that refuses a position produces a case
   * that simply stops, with no server-side trace of why. The SPA blocks
   * locally, so not even the request log sees it.
   */
  stepKind: 'document' | 'liveness' | 'geolocation';
  reason: DocumentReason | LivenessReason | GeolocationReason;
  origin: 'client' | 'server';
  attemptKey: string;
  /** Non-PII only. NEVER image bytes / base64 / data URLs. */
  meta?: Record<string, string | number>;
}

/**
 * Report ONE failed customer attempt. Returns `void` and is never awaited —
 * outcome is always the wire "fail", `reason` maps to `reason_code`, and
 * `{ origin, ...meta }` becomes the non-PII `metadata` object. Any transport
 * error is swallowed so the applicant flow is never blocked or errored.
 */
export function reportAttempt(token: string, r: AttemptReport): void {
  // Client-side dedup: same logical attempt reported twice → one send.
  if (sent.has(r.attemptKey)) return;
  sent.add(r.attemptKey);

  try {
    const body = JSON.stringify({
      step_key: r.stepKey,
      step_kind: r.stepKind,
      outcome: 'fail',
      reason_code: r.reason,
      attempt_key: r.attemptKey,
      metadata: { origin: r.origin, ...r.meta },
    });

    void fetch(ATTEMPTS_URL, {
      method: 'POST',
      keepalive: true,
      headers: { authorization: `Bearer ${token}`, 'content-type': 'application/json' },
      body,
    }).catch(() => undefined); // swallow rejection — fire-and-forget
  } catch {
    // Swallow any synchronous throw (e.g. fetch unavailable / serialize error).
    // A failed report must never surface to the applicant.
  }
}

/**
 * Test-only hook to clear the module-level dedup set between cases. Not used in
 * production (the SPA is single-case per load).
 */
export function __resetSentAttempts(): void {
  sent.clear();
}
